import React, { useState, useEffect, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize from 'rehype-sanitize';
import { Eye, EyeOff, Bold, Italic, Link, List, Quote, Code, Image, Hash } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { SelectWithAdd } from '../ui/select-with-add';
import { DependentSelect } from '../ui/dependent-select';
import { ReviewPeriodSelect } from '../ui/review-period-select';
import { api } from '../../lib/api';

interface MarkdownEditorProps {
  title?: string;
  content?: string;
  description?: string;
  category?: string;
  nis2_chapter?: string; // Keep for backward compatibility
  nis2_article?: string;
  nis2_letter?: string;
  tags?: string[];
  review_date?: string;
  review_period?: string;
  onSave: (data: {
    title: string;
    content: string;
    description?: string;
    category?: string;
    nis2_chapter?: string; // Keep for backward compatibility
    nis2_article?: string;
    nis2_letter?: string;
    tags?: string[];
    review_date?: string;
    review_period?: string;
    change_notes?: string;
  }) => void;
  onCancel: () => void;
  loading?: boolean;
  categories?: Array<{ value: string; label: string; description: string }>;
  nis2Chapters?: Array<{ value: string; label: string }>;
}

export function MarkdownEditor({
  title = '',
  content = '',
  description = '',
  category = '',
  nis2_chapter = '', // Keep for backward compatibility
  nis2_article = '',
  nis2_letter = '',
  tags = [],
  review_date = '',
  review_period = '1y',
  onSave,
  onCancel,
  loading = false,
  categories = [],
  nis2Chapters = []
}: MarkdownEditorProps) {
  // Helper function to remove YAML frontmatter from content
  const removeMetadata = useCallback((content: string) => {
    if (!content) return '';
    const lines = content.split('\n');
    
    // Check if content starts with YAML frontmatter
    if (lines[0] !== '---') {
      return content;
    }
    
    // Find end of YAML frontmatter
    let endIndex = -1;
    for (let i = 1; i < lines.length; i++) {
      if (lines[i] === '---') {
        endIndex = i;
        break;
      }
    }
    
    if (endIndex === -1) {
      return content;
    }
    
    // Return content after frontmatter, removing any leading empty lines
    const contentLines = lines.slice(endIndex + 1);
    while (contentLines.length > 0 && contentLines[0].trim() === '') {
      contentLines.shift();
    }
    
    return contentLines.join('\n');
  }, []);

  // Helper function to calculate review date from period
  const calculateReviewDate = useCallback((reviewPeriod: string, fromDate?: string): string => {
    if (!reviewPeriod) return '';
    
    const startDate = fromDate ? new Date(fromDate) : new Date();
    if (isNaN(startDate.getTime())) return '';
    
    // Parse period (e.g., '1y', '6m', '30d')
    const match = reviewPeriod.match(/^(\d+)([dmy])$/i);
    if (!match) return '';
    
    const value = parseInt(match[1], 10);
    const unit = match[2].toLowerCase();
    
    let days = 0;
    switch (unit) {
      case 'd':
        days = value;
        break;
      case 'm':
        days = value * 30; // Approximate month as 30 days
        break;
      case 'y':
        days = value * 365; // Approximate year as 365 days
        break;
      default:
        return '';
    }
    
    const reviewDate = new Date(startDate);
    reviewDate.setDate(reviewDate.getDate() + days);
    
    return reviewDate.toISOString().split('T')[0]; // Return YYYY-MM-DD format
  }, []);

  // Initialize formData with auto-calculated review_date if needed
  const initializeFormData = () => {
    const initialReviewPeriod = review_period || '1y';
    const initialReviewDate = review_date || '';
    
    // Auto-calculate review_date for new documents (no title prop means create mode)
    const shouldAutoCalculate = !title && !initialReviewDate && initialReviewPeriod;
    const calculatedReviewDate = shouldAutoCalculate ? calculateReviewDate(initialReviewPeriod) : initialReviewDate;
    
    return {
      title: title || '',
      content: removeMetadata(content || ''),
      description: description || '',
      category: category || '',
      nis2_chapter: nis2_chapter || '', // Keep for backward compatibility
      nis2_article: nis2_article || '',
      nis2_letter: nis2_letter || '',
      tags: tags ? [...tags] : [],
      review_date: calculatedReviewDate,
      review_period: initialReviewPeriod,
      change_notes: ''
    };
  };

  const [formData, setFormData] = useState(initializeFormData);
  const [showPreview, setShowPreview] = useState(false);
  const [newTag, setNewTag] = useState('');


  // Update form data when props change (for edit mode)
  // Only update if we have actual props values (not for create mode)
  useEffect(() => {
    // Only update form data if we have title prop (indicates edit mode)
    if (title !== undefined && title !== '') {
      setFormData(prev => ({
        title: title || '',
        content: removeMetadata(content || ''),
        description: description || '',
        category: category || '',
        nis2_chapter: nis2_chapter || '', // Keep for backward compatibility
        nis2_article: nis2_article || '',
        nis2_letter: nis2_letter || '',
        tags: tags ? [...tags] : [],
        review_date: review_date || '',
        review_period: review_period || '1y',
        change_notes: prev.change_notes // Keep change notes during edit
      }));
      
    }
  }, [title, content, description, category, nis2_chapter, nis2_article, nis2_letter, tags, review_date, review_period, removeMetadata, calculateReviewDate]);


  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Special handler for review_period changes that auto-calculates review_date
  const handleReviewPeriodChange = (value: string) => {
    const newReviewDate = calculateReviewDate(value);
    setFormData(prev => ({
      ...prev,
      review_period: value,
      // Only auto-set review_date if it's empty or if we're creating a new document (no title initially)
      review_date: !prev.review_date || !title ? newReviewDate : prev.review_date
    }));
  };

  const handleAddTag = () => {
    if (newTag.trim() && !formData.tags.includes(newTag.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, newTag.trim()]
      }));
      setNewTag('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const insertMarkdown = (before: string, after: string = '') => {
    const textarea = document.getElementById('content-textarea') as HTMLTextAreaElement;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = formData.content.substring(start, end);
    
    const newContent = 
      formData.content.substring(0, start) +
      before + selectedText + after +
      formData.content.substring(end);
    
    handleInputChange('content', newContent);
    
    // Reset cursor position
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(
        start + before.length,
        start + before.length + selectedText.length
      );
    }, 0);
  };

  // Wrap ASCII tables (bounded by dashed lines) into fenced code blocks for readability
  function formatAsciiTablesAsPre(markdown: string): string {
    const lines = markdown.split('\n');
    const result: string[] = [];
    let i = 0;
    while (i < lines.length) {
      if (/^\s*-{3,}\s*$/.test(lines[i])) {
        const startIndex = i;
        i++;
        while (i < lines.length && !/^\s*-{3,}\s*$/.test(lines[i])) {
          i++;
        }
        if (i < lines.length && /^\s*-{3,}\s*$/.test(lines[i])) {
          const block = lines.slice(startIndex, i + 1).join('\n');
          result.push('```text');
          result.push(block);
          result.push('```');
          i++;
          continue;
        }
        result.push(lines[startIndex]);
      } else {
        result.push(lines[i]);
        i++;
      }
    }
    return result.join('\n');
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    return dateString.split('T')[0]; // Return YYYY-MM-DD format
  };

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">
          {title ? 'Upravit dokument' : 'Nový dokument'}
        </h1>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onCancel} disabled={loading}>
            Zrušit
          </Button>
          <Button onClick={handleSubmit} disabled={loading || !formData.title.trim() || !formData.content.trim()}>
            {loading ? 'Ukládá se...' : 'Uložit'}
          </Button>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Information */}
        <Card>
          <CardHeader>
            <CardTitle>Základní informace</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="title">Název dokumentu *</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                placeholder="Zadejte název dokumentu"
                required
              />
            </div>

            <div>
              <Label htmlFor="description">Popis</Label>
              <Input
                id="description"
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                placeholder="Krátký popis dokumentu"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <SelectWithAdd
                  label="Kategorie"
                  value={formData.category}
                  onChange={(value) => handleInputChange('category', value as string)}
                  codelistType="document_categories"
                  placeholder="Vyberte kategorii..."
                  required={true}
                  onAddItem={async (item) => {
                    await api.post('/codelists/items', {
                      codelist_type: 'document_categories',
                      code: item.code,
                      name: item.name,
                      description: item.description
                    });
                  }}
                />
              </div>

              <ReviewPeriodSelect
                value={formData.review_period || '1y'}
                onChange={handleReviewPeriodChange}
                label="Review Period"
                showPreview={true}
                currentReviewDate={formData.review_date}
              />

              <div>
                <Label htmlFor="review_date">
                  Datum příští kontroly
                  <span className="text-xs text-muted-foreground ml-2">
                    (volitelné - počítá se automaticky)
                  </span>
                </Label>
                <Input
                  id="review_date"
                  type="date"
                  value={formatDate(formData.review_date)}
                  onChange={(e) => handleInputChange('review_date', e.target.value)}
                  placeholder="Auto-calculated from review period"
                />
              </div>
            </div>

            {/* NIS2 Article & Letter - Full width for better readability */}
            <div className="space-y-2">
              <DependentSelect
                label="NIS2 Article & Letter"
                parentValue={formData.nis2_article || ''}
                childValue={formData.nis2_letter || ''}
                onParentChange={(value) => {
                  handleInputChange('nis2_article', value as string);
                  // Don't automatically reset child - let DependentSelect handle it with preserveChildOnAutoSelect
                }}
                onChildChange={(value) => handleInputChange('nis2_letter', value as string)}
                parentCodelistType="nis2_articles"
                childCodelistType="nis2_letters"
                placeholder="Select NIS2 Article & Letter..."
                preserveChildOnAutoSelect={true}
              />
            </div>

            {/* Tags */}
            <div>
              <Label>Štítky</Label>
              <div className="flex gap-2 mb-2">
                <Input
                  value={newTag}
                  onChange={(e) => setNewTag(e.target.value)}
                  placeholder="Přidat štítek"
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                />
                <Button type="button" onClick={handleAddTag} variant="outline">
                  Přidat
                </Button>
              </div>
              <div className="flex flex-wrap gap-1">
                {formData.tags.map(tag => (
                  <Badge key={tag} variant="secondary" className="cursor-pointer" onClick={() => handleRemoveTag(tag)}>
                    {tag} ×
                  </Badge>
                ))}
              </div>
            </div>

            {/* Change Notes for updates */}
            {title && (
              <div>
                <Label htmlFor="change_notes">Poznámky ke změnám</Label>
                <Input
                  id="change_notes"
                  value={formData.change_notes}
                  onChange={(e) => handleInputChange('change_notes', e.target.value)}
                  placeholder="Popište provedené změny"
                />
              </div>
            )}
          </CardContent>
        </Card>

        {/* Content Editor */}
        <Card>
          <CardHeader>
            <div className="flex justify-between items-center">
              <CardTitle>Obsah dokumentu *</CardTitle>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setShowPreview(!showPreview)}
                >
                  {showPreview ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  {showPreview ? 'Skrýt náhled' : 'Zobrazit náhled'}
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {/* Markdown Toolbar */}
            <div className="flex flex-wrap gap-1 mb-4 p-2 bg-gray-50 rounded border">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => insertMarkdown('# ')}
                title="Nadpis 1"
              >
                <Hash className="h-4 w-4" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => insertMarkdown('**', '**')}
                title="Tučný text"
              >
                <Bold className="h-4 w-4" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => insertMarkdown('*', '*')}
                title="Kurzíva"
              >
                <Italic className="h-4 w-4" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => insertMarkdown('[', '](url)')}
                title="Odkaz"
              >
                <Link className="h-4 w-4" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => insertMarkdown('- ')}
                title="Seznam"
              >
                <List className="h-4 w-4" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => insertMarkdown('> ')}
                title="Citace"
              >
                <Quote className="h-4 w-4" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => insertMarkdown('`', '`')}
                title="Kód"
              >
                <Code className="h-4 w-4" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => insertMarkdown('![alt](', ')')}
                title="Obrázek"
              >
                <Image className="h-4 w-4" />
              </Button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Editor */}
              <div className={showPreview ? '' : 'lg:col-span-2'}>
                <textarea
                  id="content-textarea"
                  value={formData.content}
                  onChange={(e) => handleInputChange('content', e.target.value)}
                  placeholder="Začněte psát obsah dokumentu v Markdown formátu..."
                  className="w-full h-96 p-4 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                  required
                />
                <div className="text-xs text-gray-500 mt-1">
                  Podporuje Markdown syntaxi: **tučný**, *kurzíva*, # nadpisy, - seznamy, atd.
                </div>
              </div>

              {/* Preview */}
              {showPreview && (
                <div className="border border-gray-300 rounded-md p-4 bg-gray-50">
                  <div className="text-sm font-medium text-gray-700 mb-2">Náhled:</div>
                  <div className="prose prose-sm max-w-none">
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      rehypePlugins={[rehypeRaw, rehypeSanitize]}
                    >
                      {formatAsciiTablesAsPre(formData.content)}
                    </ReactMarkdown>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </form>
    </div>
  );
}
